import { Task } from "@common-models/task";
import { TaskColumn } from "@common-models/task-column";
import { tasksSelectors } from "@common-reducers/DBServiceThunks";
import { columnListForDisplaySelector } from "@common-reducers/TaskSelectors";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ColumnCell } from "@common-components/tasks/task-row/column-cell";
import ExtraDataButton from "@common-components/tasks/extra-data-button/extraDataButton";
import { columnTypeMap } from "@common-components/tasks/columns-cells/column-cell-defs";
import classes from "./mobile-board.module.scss";

interface Props {
  taskId: string;
  colorAccent?: string;
  isSubTask?: boolean;
}

// Column types that are structural / not meaningful as a labeled card field.
const EXCLUDED_TYPES = new Set<string>([
  "context-menu",
  "row-prefix",
  "row-suffix",
  "group-cell",
  "board-cell",
  "empty-cell",
]);

// Columns shown inline on the card by default (Monday-style priority order).
const INLINE_RANK: Record<string, number> = {
  "person-picker": 0,
  "status-option-picker": 1,
  "option-picker": 2,
  "chat": 3,
  "datepicker": 4,
  "timeline-picker": 5,
  "complexity-picker": 6,
};

function columnLabel(column: TaskColumn): string {
  return column.title || columnTypeMap.get(column.type ?? "")?.title || "";
}

function MobileTaskCard({ taskId, colorAccent, isSubTask = false }: Props) {
  const task: Task = useSelector((state) => tasksSelectors.selectById(state, taskId));
  const columnList = useAppSelector((state) =>
    columnListForDisplaySelector(state, task?.boardId, task?.groupId)
  ) as TaskColumn[];

  const [showMore, setShowMore] = useState(false);

  const nameColumn = useMemo(
    () => (columnList ?? []).find((col) => col.type === "row-prefix"),
    [columnList]
  );

  const { inlineColumns, moreColumns } = useMemo(() => {
    const dataColumns = (columnList ?? []).filter(
      (col) => col.type && !EXCLUDED_TYPES.has(col.type)
    );

    const inline: TaskColumn[] = [];
    const more: TaskColumn[] = [];
    for (const col of dataColumns) {
      if (col.type && col.type in INLINE_RANK) {
        inline.push(col);
      } else {
        more.push(col);
      }
    }
    inline.sort(
      (a, b) => (INLINE_RANK[a.type ?? ""] ?? 99) - (INLINE_RANK[b.type ?? ""] ?? 99)
    );
    return { inlineColumns: inline, moreColumns: more };
  }, [columnList]);

  const accent = colorAccent || "#00c875";

  if (!task) return null;

  const renderField = (column: TaskColumn) => (
    <div className={classes.field} key={column._id}>
      <span className={classes.fieldLabel}>{columnLabel(column)}</span>
      <div className={classes.fieldValue}>
        <ColumnCell column={column} taskId={taskId} />
      </div>
    </div>
  );

  return (
    <div className={`${classes.card} ${isSubTask ? classes.subCard : ""}`}>
      <div className={classes.accentBar} style={{ background: accent }} />

      <div className={classes.cardHeader}>
        {/* Reuse the desktop name/prefix cell: checkbox + inline-rename + subtask toggle */}
        <div className={classes.headerPrefix}>
          {nameColumn && <ColumnCell column={nameColumn} taskId={taskId} />}
        </div>
        {/* Same "open task detail" mechanism the desktop row uses */}
        <div className={classes.openBtn}>
          <ExtraDataButton taskId={taskId} />
        </div>
      </div>

      <div className={classes.fields}>
        {inlineColumns.map(renderField)}
        {showMore && moreColumns.map(renderField)}
        {moreColumns.length > 0 && (
          <button
            type="button"
            className={classes.moreBtn}
            onClick={() => setShowMore((v) => !v)}
          >
            {showMore ? "Show less" : `Show ${moreColumns.length} more`}
          </button>
        )}
      </div>
    </div>
  );
}

export default memo(MobileTaskCard);
